import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import ActionPicker, { useActionFavorites } from './ActionPicker';
import { API_BASE } from '../../../config/api';

// World (x,z) -> pixel on the displayed map image. Same math as the session-playback
// map in AnalyticsAdmin, kept local so this panel is self-contained.
function worldToMapPoint(point, mapConfig, width, height) {
  if (!point || !mapConfig || !width || !height) return null;
  const minX = Number(mapConfig.worldMinX);
  const maxX = Number(mapConfig.worldMaxX);
  const minZ = Number(mapConfig.worldMinZ);
  const maxZ = Number(mapConfig.worldMaxZ);
  const rotationDegrees = Number(mapConfig.rotationDegrees || 0);
  const flipX = Boolean(mapConfig.flipX);
  const flipZ = Boolean(mapConfig.flipZ);
  if (![minX, maxX, minZ, maxZ, rotationDegrees].every(Number.isFinite)) return null;
  if (maxX <= minX || maxZ <= minZ) return null;

  const centerX = (minX + maxX) / 2;
  const centerZ = (minZ + maxZ) / 2;
  const radians = (rotationDegrees * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);

  const dx = Number(point.x) - centerX;
  const dz = Number(point.z) - centerZ;
  const rotatedX = centerX + (dx * cos - dz * sin);
  const rotatedZ = centerZ + (dx * sin + dz * cos);

  const u = (rotatedX - minX) / (maxX - minX);
  const v = (rotatedZ - minZ) / (maxZ - minZ);
  const finalU = flipX ? 1 - u : u;
  const finalV = flipZ ? 1 - v : v;

  return {
    x: Math.max(0, Math.min(1, finalU)) * width,
    y: Math.max(0, Math.min(1, finalV)) * height,
  };
}

// A 256-entry blue→cyan→green→yellow→red palette, indexed by intensity, used to
// colorize the accumulated heat buffer (the classic heatmap look).
function buildPalette() {
  const stops = [
    { at: 0.0, c: [0, 0, 0] },
    { at: 0.25, c: [40, 90, 220] },
    { at: 0.45, c: [40, 200, 200] },
    { at: 0.6, c: [70, 220, 90] },
    { at: 0.78, c: [240, 220, 60] },
    { at: 1.0, c: [235, 55, 45] },
  ];
  const lut = new Uint8ClampedArray(256 * 3);
  for (let i = 0; i < 256; i += 1) {
    const t = i / 255;
    let a = stops[0];
    let b = stops[stops.length - 1];
    for (let s = 0; s < stops.length - 1; s += 1) {
      if (t >= stops[s].at && t <= stops[s + 1].at) {
        a = stops[s];
        b = stops[s + 1];
        break;
      }
    }
    const span = b.at - a.at || 1;
    const k = (t - a.at) / span;
    lut[i * 3] = a.c[0] + (b.c[0] - a.c[0]) * k;
    lut[i * 3 + 1] = a.c[1] + (b.c[1] - a.c[1]) * k;
    lut[i * 3 + 2] = a.c[2] + (b.c[2] - a.c[2]) * k;
  }
  return lut;
}

// windowValue ('1' | '7' | '30' | 'all') comes from the Analytics top bar.
const HeatmapPanel = ({ projectId, fetchJson, userToken, windowValue, onOpenMapSettings }) => {

  const [actions, setActions] = useState([]);
  const [actionsLoading, setActionsLoading] = useState(false);
  const [actionsError, setActionsError] = useState('');
  const [selected, setSelected] = useState('');
  const favoritesState = useActionFavorites(projectId, fetchJson);

  const [projectMap, setProjectMap] = useState(null);
  const [mapLoading, setMapLoading] = useState(false);
  const [mapImageSize, setMapImageSize] = useState({ width: 0, height: 0 });

  const [heat, setHeat] = useState(null);
  const [heatLoading, setHeatLoading] = useState(false);
  const [heatError, setHeatError] = useState('');

  const canvasRef = useRef(null);
  const latestActionsPath = useRef('');
  const latestHeatUrl = useRef('');
  const palette = useMemo(() => buildPalette(), []);

  const windowParams = useCallback(() => {
    const params = new URLSearchParams();
    if (windowValue === 'all') params.set('days', 'all');
    else params.set('days', windowValue);
    return params;
  }, [windowValue]);

  // Load the project map once per project.
  useEffect(() => {
    if (!projectId) return;
    setMapLoading(true);
    setMapImageSize({ width: 0, height: 0 });
    fetchJson(`/api/analytics/projects/${projectId}/map`)
      .then((data) => setProjectMap(data))
      .catch(() => setProjectMap(null))
      .finally(() => setMapLoading(false));
  }, [projectId, fetchJson]);

  // Load the action list for the window.
  useEffect(() => {
    if (!projectId) return undefined;
    const path = `/api/analytics/projects/${projectId}/funnel/actions?${windowParams().toString()}`;
    if (path === latestActionsPath.current) return undefined;
    const timer = setTimeout(() => {
      latestActionsPath.current = path;
      setActionsLoading(true);
      setActionsError('');
      fetchJson(path)
        .then((data) => {
          if (path !== latestActionsPath.current) return;
          setActions(Array.isArray(data.actions) ? data.actions : []);
        })
        .catch((err) => {
          if (path !== latestActionsPath.current) return;
          latestActionsPath.current = '';
          setActionsError(err.message || 'Could not load actions');
          setActions([]);
        })
        .finally(() => {
          if (path === latestActionsPath.current || latestActionsPath.current === '') {
            setActionsLoading(false);
          }
        });
    }, 180);
    return () => clearTimeout(timer);
  }, [projectId, windowParams, fetchJson]);

  // Keep the selection valid against the current list.
  useEffect(() => {
    const names = new Set(actions.map((a) => a.action));
    setSelected((prev) => (prev && names.has(prev) ? prev : ''));
  }, [actions]);

  // Fetch the heatmap for the selected action + window.
  useEffect(() => {
    if (!projectId || !selected) {
      latestHeatUrl.current = '';
      setHeat(null);
      return undefined;
    }
    const params = windowParams();
    params.set('action', selected);
    const url = `/api/analytics/projects/${projectId}/funnel/heatmap?${params.toString()}`;
    if (url === latestHeatUrl.current) return undefined;
    const timer = setTimeout(() => {
      latestHeatUrl.current = url;
      setHeatLoading(true);
      setHeatError('');
      fetchJson(url)
        .then((data) => {
          if (url !== latestHeatUrl.current) return;
          setHeat(data);
        })
        .catch((err) => {
          if (url !== latestHeatUrl.current) return;
          latestHeatUrl.current = '';
          setHeatError(err.message || 'Could not load heatmap');
          setHeat(null);
        })
        .finally(() => {
          if (url === latestHeatUrl.current || latestHeatUrl.current === '') {
            setHeatLoading(false);
          }
        });
    }, 180);
    return () => clearTimeout(timer);
  }, [projectId, selected, windowParams, fetchJson]);

  // Paint the heatmap onto the canvas whenever the data, map, or size changes.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const { width, height } = mapImageSize;
    const ctx = canvas.getContext('2d');
    if (!width || !height) {
      ctx?.clearRect(0, 0, canvas.width, canvas.height);
      return;
    }
    canvas.width = width;
    canvas.height = height;
    ctx.clearRect(0, 0, width, height);

    const cells = heat?.cells || [];
    const grid = Number(heat?.grid || 12);
    const maxCount = Number(heat?.maxCount || 0);
    if (!cells.length || !maxCount || !projectMap) return;

    // Pixel size of one grid cell, used to scale the blob radius so density reads
    // consistently regardless of map resolution.
    const spanX = Number(projectMap.worldMaxX) - Number(projectMap.worldMinX);
    const spanZ = Number(projectMap.worldMaxZ) - Number(projectMap.worldMinZ);
    const cellPxX = spanX > 0 ? (grid / spanX) * width : 6;
    const cellPxZ = spanZ > 0 ? (grid / spanZ) * height : 6;
    const radius = Math.max(6, Math.max(cellPxX, cellPxZ) * 2.4);

    // 1) Accumulate grayscale intensity blobs (additive) into an offscreen buffer.
    const off = document.createElement('canvas');
    off.width = width;
    off.height = height;
    const octx = off.getContext('2d');
    octx.globalCompositeOperation = 'lighter';
    // Log scale keeps a few super-hot cells from washing everything else out.
    const denom = Math.log1p(maxCount) || 1;
    for (const cell of cells) {
      const world = { x: cell.gx * grid, z: cell.gy * grid };
      const p = worldToMapPoint(world, projectMap, width, height);
      if (!p) continue;
      const intensity = Math.min(1, Math.log1p(Number(cell.count || 0)) / denom);
      const alpha = 0.15 + intensity * 0.85;
      const g = octx.createRadialGradient(p.x, p.y, 0, p.x, p.y, radius);
      g.addColorStop(0, `rgba(0,0,0,${alpha})`);
      g.addColorStop(1, 'rgba(0,0,0,0)');
      octx.fillStyle = g;
      octx.beginPath();
      octx.arc(p.x, p.y, radius, 0, Math.PI * 2);
      octx.fill();
    }

    // 2) Colorize the accumulated alpha through the palette.
    const img = octx.getImageData(0, 0, width, height);
    const d = img.data;
    for (let i = 0; i < d.length; i += 4) {
      const a = d[i + 3];
      if (a === 0) continue;
      const idx = a * 3;
      d[i] = palette[idx];
      d[i + 1] = palette[idx + 1];
      d[i + 2] = palette[idx + 2];
      d[i + 3] = Math.min(235, a + 25);
    }
    octx.putImageData(img, 0, 0);
    ctx.drawImage(off, 0, 0);
  }, [heat, projectMap, mapImageSize, palette]);

  const mapImageUrl = projectMap?.hasImage
    ? `${API_BASE}${projectMap.imageUrl}?token=${encodeURIComponent(userToken || '')}`
    : null;

  const totalEvents = Number(heat?.totalEvents || 0);
  const hotspots = heat?.cells?.length || 0;
  const peak = Number(heat?.maxCount || 0);

  return (
    <Layout>
      {/* Left rail: searchable action list */}
      <Rail>

        {actionsError ? (
          <Note $error>{actionsError}</Note>
        ) : actions.length === 0 ? (
          <Note>{actionsLoading ? 'Loading actions…' : 'No actions in this range.'}</Note>
        ) : (
          <ActionPicker
            actions={actions}
            favorites={favoritesState}
            isSelected={(name) => selected === name}
            onPick={setSelected}
            height="30rem"
          />
        )}
      </Rail>

      {/* Right: map + heat overlay */}
      <Stage>
        {!selected ? (
          <Placeholder>
            <PlaceholderTitle>Pick an action</PlaceholderTitle>
            <PlaceholderText>
              Choose an action on the left to see where in the world players trigger it.
            </PlaceholderText>
          </Placeholder>
        ) : mapLoading ? (
          <Placeholder><PlaceholderText>Loading map…</PlaceholderText></Placeholder>
        ) : !projectMap?.hasImage ? (
          <Placeholder>
            <PlaceholderTitle>No map yet</PlaceholderTitle>
            <PlaceholderText>
              Upload a top-down image of your place in Project settings → Map. Heatmaps are then drawn on top of it.
            </PlaceholderText>
            {onOpenMapSettings && (
              <SetupButton type="button" onClick={onOpenMapSettings}>
                Configure the map
              </SetupButton>
            )}
          </Placeholder>
        ) : (
          <>
            <StatBar>
              <Stat>
                <StatValue>{totalEvents.toLocaleString()}</StatValue>
                <StatLabel>total events</StatLabel>
              </Stat>
              <Stat>
                <StatValue>{hotspots.toLocaleString()}</StatValue>
                <StatLabel>hotspots</StatLabel>
              </Stat>
              <Stat>
                <StatValue>{peak.toLocaleString()}</StatValue>
                <StatLabel>busiest spot</StatLabel>
              </Stat>
              <RangeTag>
                {heatLoading ? 'Updating…' : `${heat?.from || 'start'} to ${heat?.to || 'today'}`}
              </RangeTag>
            </StatBar>

            <MapFrame>
              <MapInner>
                <MapImg
                  src={mapImageUrl}
                  alt="Project map"
                  onLoad={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    setMapImageSize({ width: rect.width, height: rect.height });
                  }}
                />
                <HeatCanvas ref={canvasRef} />
              </MapInner>
              {heat && hotspots === 0 && !heatLoading && (
                <EmptyOverlay>No location data recorded for this action yet.</EmptyOverlay>
              )}
            </MapFrame>

            {heatError && <Note $error style={{ marginTop: '0.6rem' }}>{heatError}</Note>}

            <Legend>
              <LegendLabel>Fewer</LegendLabel>
              <LegendBar />
              <LegendLabel>More</LegendLabel>
            </Legend>
          </>
        )}
      </Stage>
    </Layout>
  );
};

/* ---------- styles (match FunnelPanel / AnalyticsAdmin theme) ---------- */

const Layout = styled.div`
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 1.1rem;
  align-items: start;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const Rail = styled.div`
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 0.75rem;
  padding: 0.9rem;
  position: sticky;
  top: 1rem;
`;



const Stage = styled.div`
  min-width: 0;
`;

const StatBar = styled.div`
  display: flex;
  align-items: center;
  gap: 1.6rem;
  flex-wrap: wrap;
  margin-bottom: 0.85rem;
`;

const Stat = styled.div`
  display: grid;
  gap: 0.1rem;
`;

const StatValue = styled.div`
  color: var(--text-primary);
  font-size: 1.35rem;
  font-weight: 650;
  font-family: var(--font-display);
  letter-spacing: -0.02em;
  line-height: 1.1;
`;

const StatLabel = styled.div`
  color: var(--text-tertiary);
  font-size: 0.74rem;
`;

const RangeTag = styled.div`
  margin-left: auto;
  color: var(--text-tertiary);
  font-size: 0.75rem;
  padding: 0.3rem 0.6rem;
  border: 1px solid var(--border-subtle);
  border-radius: 6px;
  white-space: nowrap;
`;

const MapFrame = styled.div`
  position: relative;
  border: 1px solid var(--border);
  border-radius: 0.75rem;
  overflow: hidden;
  background: var(--surface-1);
`;

const MapInner = styled.div`
  position: relative;
  display: block;
  line-height: 0;
`;

const MapImg = styled.img`
  display: block;
  width: 100%;
  height: auto;
`;

const HeatCanvas = styled.canvas`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
`;

const EmptyOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  font-size: 0.85rem;
  background: color-mix(in srgb, var(--card) 55%, transparent);
`;

const Legend = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-top: 0.7rem;
`;

const LegendLabel = styled.span`
  color: var(--text-tertiary);
  font-size: 0.72rem;
`;

const LegendBar = styled.div`
  flex: 1;
  max-width: 240px;
  height: 8px;
  border-radius: 999px;
  background: linear-gradient(90deg,
    rgb(40, 90, 220) 0%,
    rgb(40, 200, 200) 28%,
    rgb(70, 220, 90) 48%,
    rgb(240, 220, 60) 72%,
    rgb(235, 55, 45) 100%);
`;

const Placeholder = styled.div`
  border: 1px dashed var(--border);
  border-radius: 10px;
  padding: 3rem 1.5rem;
  text-align: center;
  display: grid;
  gap: 0.5rem;
  justify-items: center;
`;

const PlaceholderTitle = styled.div`
  color: var(--text-primary);
  font-size: 0.95rem;
  font-weight: 600;
`;

const PlaceholderText = styled.div`
  color: var(--text-tertiary);
  font-size: 0.85rem;
  max-width: 380px;
  line-height: 1.55;
`;

const Note = styled.div`
  color: ${(p) => (p.$error ? 'var(--error)' : 'var(--text-tertiary)')};
  font-size: 0.82rem;
  line-height: 1.5;
`;

export default HeatmapPanel;

const SetupButton = styled.button`
  margin-top: 1rem;
  min-height: 2.5rem;
  padding: 0 1rem;
  border: 1px solid var(--border-accent);
  border-radius: 0.375rem;
  background: transparent;
  color: var(--text-primary);
  font-size: 0.875rem;
`;
